export function resolveConfirmationCaseId(confirmation) {
  const raw = confirmation?.caseId;
  if (!raw) return null;
  if (typeof raw === "object") {
    return raw._id || raw.id || null;
  }
  return String(raw);
}

/** Minimal case shape when full case API is slow or unavailable. */
export function buildFallbackCaseFromConfirmation(confirmation) {
  if (!confirmation) return null;
  const id = resolveConfirmationCaseId(confirmation);
  return {
    _id: id,
    caseCode: confirmation.caseCode || "",
    vehicleNumber: confirmation.vehicleNumber || "",
    customerName: confirmation.customerName || "",
    bankName: confirmation.bankName || "",
    branchName: confirmation.branchName || "",
  };
}

function patchVehicleKeys(fields, plate) {
  if (!fields || typeof fields !== "object") return fields;
  const out = { ...fields };
  for (const key of Object.keys(out)) {
    if (String(key).startsWith("_")) continue;
    if (/registration\s*numbers?|vehicle\s*(no\.?|number)|regn?\s*(no|number)|vrn/i.test(key)) {
      out[key] = plate;
    }
  }
  return out;
}

/** Keep Excel/case registration in sync with the vehicle actually traced on this confirmation. */
export function applyTracedVehicleToCase(caseData, confirmation) {
  if (!caseData) return caseData;
  const plate = String(confirmation?.vehicleNumber || caseData.vehicleNumber || "").trim();
  if (!plate) return caseData;
  return {
    ...caseData,
    vehicleNumber: plate,
    excelFields: patchVehicleKeys(caseData.excelFields, plate),
    extraFields: patchVehicleKeys(caseData.extraFields, plate),
  };
}

export const CONFIRMATION_VIEWS = {
  ALL: "",
  INVENTORY_CONFIRMED: "inventory-confirmed",
  INVENTORY_PENDING_APPROVAL: "inventory-pending",
};

export function getInventoryStatus(item) {
  if (String(item?.status || "").toUpperCase() !== "CONFIRMED") {
    return { key: "na", label: "—", badgeClass: "cf-badge" };
  }
  if (item.inventoryRevisionRequested) {
    return {
      key: "revision",
      label: "Update needed",
      badgeClass: "cf-badge cf-inv-badge cf-badge--pending",
    };
  }
  if (item.inventoryConfirmed) {
    return {
      key: "confirmed",
      label: "Confirmed",
      badgeClass: "cf-badge cf-inv-badge cf-badge--confirmed",
    };
  }
  if (item.inventorySubmitted) {
    return {
      key: "pending-approval",
      label: "Pending approval",
      badgeClass: "cf-badge cf-inv-badge cf-badge--pending",
    };
  }
  return {
    key: "pending-upload",
    label: "Not uploaded",
    badgeClass: "cf-badge cf-inv-badge cf-badge--pending",
  };
}

function listItemSortMs(item, listFilter, view) {
  const created = Date.parse(item?.createdAt) || 0;
  if (view === CONFIRMATION_VIEWS.INVENTORY_CONFIRMED) {
    return Date.parse(item?.inventoryConfirmedAt) || created;
  }
  if (view === CONFIRMATION_VIEWS.INVENTORY_PENDING_APPROVAL) {
    return created;
  }
  if (String(listFilter || "").toUpperCase() === "CONFIRMED") {
    return Date.parse(item?.reviewedAt) || created;
  }
  return created;
}

function confirmationSortId(item) {
  const value = Number(item?._id ?? item?.id);
  return Number.isFinite(value) ? value : 0;
}

/** Newest first; tie-break by id so order is stable. Cancel list is last id first. */
export function sortConfirmationsNewestFirst(items, listFilter, view) {
  const cancelled = String(listFilter || "").toUpperCase() === "REJECTED";
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    if (cancelled) return confirmationSortId(b) - confirmationSortId(a);
    const diff = listItemSortMs(b, listFilter, view) - listItemSortMs(a, listFilter, view);
    if (diff !== 0) return diff;
    return confirmationSortId(b) - confirmationSortId(a);
  });
}

export function applyConfirmationViewFilter(items, view) {
  const list = Array.isArray(items) ? items : [];
  if (view === CONFIRMATION_VIEWS.INVENTORY_CONFIRMED) {
    return list.filter((item) => Boolean(item.inventoryConfirmed));
  }
  if (view === CONFIRMATION_VIEWS.INVENTORY_PENDING_APPROVAL) {
    return list.filter(
      (item) =>
        String(item?.status || "").toUpperCase() === "CONFIRMED" &&
        item.inventorySubmitted &&
        !item.inventoryConfirmed
    );
  }
  return list;
}

export function matchesConfirmationSearch(item, query, isRepoAdmin = true) {
  const q = String(query || "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  const haystack = [
    item?.vehicleNumber,
    item?.customerName,
    item?.chassisNumber,
    item?.engineNumber,
    item?.loanAccountNumber,
    ...(isRepoAdmin ? [item?.bankName, item?.branchName] : []),
    item?.caseCode,
    item?.requestedByName,
    item?.requestedByPhone,
    item?.requestNote,
  ]
    .map((v) => String(v || "").toLowerCase())
    .join(" ");
  return haystack.includes(q);
}

export function confirmationStatusLabel(status) {
  if (String(status || "").toUpperCase() === "REJECTED") return "Cancel";
  return status || "—";
}

export function getListEmptyMessage(listFilter, view, isRepoAdmin) {
  if (view === CONFIRMATION_VIEWS.INVENTORY_CONFIRMED) {
    return "No cases with admin-confirmed inventory yet.";
  }
  if (view === CONFIRMATION_VIEWS.INVENTORY_PENDING_APPROVAL) {
    return "No inventory waiting for your approval.";
  }
  if (listFilter === "CONFIRMED") {
    return isRepoAdmin
      ? "No confirmed traces yet. Confirmed cases appear here after you approve field reports."
      : "No confirmed traces yet. After admin confirms your report, it will show here.";
  }
  if (listFilter === "PENDING") {
    return isRepoAdmin
      ? "No pending traces awaiting review."
      : "No pending trace reports.";
  }
  if (listFilter === "REJECTED") {
    return "No cancelled confirmations.";
  }
  return "No confirmations found.";
}

export function getListSectionTitle(listFilter, view) {
  if (view === CONFIRMATION_VIEWS.INVENTORY_CONFIRMED) {
    return "Seized Successfully";
  }
  if (view === CONFIRMATION_VIEWS.INVENTORY_PENDING_APPROVAL) {
    return "Inventory to review";
  }
  if (listFilter === "PENDING") {
    return "Pending confirmations";
  }
  if (listFilter === "CONFIRMED") {
    return "Confirmed cases";
  }
  if (listFilter === "REJECTED") {
    return "Cancel confirmations";
  }
  return "Confirmations";
}

export function getListSectionHint(listFilter, view, isRepoAdmin) {
  if (view === CONFIRMATION_VIEWS.INVENTORY_CONFIRMED) {
    return "Cases where you approved tracer inventory uploads.";
  }
  if (view === CONFIRMATION_VIEWS.INVENTORY_PENDING_APPROVAL) {
    return "Review uploaded files and tap Seized inventory on each case.";
  }
  if (listFilter === "PENDING") {
    return isRepoAdmin
      ? "WhatsApp, email, SMS, and in-app traces waiting for your decision."
      : "Waiting for repo admin to confirm your field trace.";
  }
  if (listFilter === "CONFIRMED") {
    return isRepoAdmin
      ? "Approved traces — open a case and tap Seized inventory, or notify the bank / NBFC."
      : "Upload or update inventory pre/post for each confirmed vehicle.";
  }
  if (listFilter === "REJECTED") {
    return "Vehicle traces cancelled by the repo admin. Status is Cancel.";
  }
  return "Browse and open any confirmation below.";
}
